import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "../../context/AuthContext";

interface LoginProps {
	onRegisterClick?: () => void;
}

export function Login({ onRegisterClick }: LoginProps) {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const { login, user, isLoading: authLoading } = useAuth();
	const [, setLocation] = useLocation();

	// Redirect super admins to admin dashboard if they login from regular login
	useEffect(() => {
		if (!authLoading && user && user.role === "superAdmin") {
			setLocation("/n-r-ops/dashboard");
		}
	}, [user, authLoading, setLocation]);

	const handleRegisterClick = () => {
		if (onRegisterClick) {
			onRegisterClick();
		} else {
			setLocation("/signup");
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		// Validate inputs
		if (!email || !password) {
			setError("Please enter both email and password");
			return;
		}

		setIsLoading(true);

		try {
			await login(email, password);
			// Redirect will be handled by useEffect when user context updates
			// For non-super-admin users, redirect to dashboard
			if (!user || user.role !== "superAdmin") {
				setLocation("/dashboard");
			}
		} catch (err: any) {
			const errorMessage = err instanceof Error ? err.message : "Login failed";
			setError(errorMessage);

			// Check if error has redirectTo field and redirect accordingly
			if (err?.redirectTo) {
				setTimeout(() => {
					setLocation(err.redirectTo);
				}, 2000); // Show error message for 2 seconds before redirecting
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<div>
				<label
					htmlFor="email"
					className="block text-sm font-medium text-foreground mb-2"
				>
					Email
				</label>
				<input
					id="email"
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
					placeholder="you@example.com"
					autoComplete="email"
					required
				/>
			</div>

			<div>
				<label
					htmlFor="password"
					className="block text-sm font-medium text-foreground mb-2"
				>
					Password
				</label>
				<input
					id="password"
					type="password"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
					placeholder="Enter your password"
					autoComplete="current-password"
					required
				/>
			</div>

			{error && (
				<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
					{error}
				</div>
			)}

			<button
				type="submit"
				disabled={isLoading}
				className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md font-medium hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
			>
				{isLoading ? "Signing In..." : "Sign In"}
			</button>

			<div className="mt-6 text-center text-sm text-muted-foreground">
				Don't have an account?{" "}
				<button
					type="button"
					className="text-primary underline cursor-pointer hover:text-primary/80 transition-colors bg-transparent border-none p-0 font-inherit"
					onClick={handleRegisterClick}
				>
					Sign up
				</button>
			</div>
		</form>
	);
}
